export interface Module {
  id: string;
  name: string;
  slug: ModuleSlug;
  description: string;
  icon: string;
  version: string;
  is_active: boolean;
  pricing: ModulePricing;
}

// Built-in module slugs (known at compile time)
export type BuiltInModuleSlug =
  | 'lexshift'
  | 'neurosense'
  | 'waterandice'
  | 'production'
  | 'gamification'
  | 'agenda'
  | 'provisio'
  | 'fabrica'
  | 'nexus'
  // Organ wzroku (kamery przemysłowe). Tabele w bazie noszą prefiks
  // `nexus_eye_*` — slug modułu i nazwa organu rozchodzą się tak samo jak
  // przy `waterandice` → TEMPERATIO i `gamification` → NEXUS AEGIS.
  | 'conspectus'
  // Organ robotów i ich edukacji (Akademia Zawodów, flota, cyfrowy bliźniak).
  // Tabele nowe noszą prefiks `artifex_*`, ale katalog zawodów i zgody zostają
  // na `academy_*` — ten prefiks nigdy nie należał do FABRYKI, a `academy_consents`
  // jest dziś rejestrem zgód biometrycznych całej platformy (migracje 420/422).
  // Serwisy `robot-edge-agent`, `isaac-trainer` i `twin-core` należą do tego
  // organu mimo innych nazw katalogów — tak samo jak `nexus-eye-gateway` należy
  // do Conspectusa. Powody: docs/13-ARTIFEX/.
  | 'artifex'
  // Organ pieca piekarskiego (plan komór, wsady, hala na tablecie). Tabele
  // `furnus_*` (migracje 734–737, 740); panel `apps/module-furnus`, hala
  // `:feature:furnus` aplikacji mobilnej.
  | 'furnus'
  | 'ucs';

// ModuleSlug accepts both built-in and dynamic (partner) module slugs
export type ModuleSlug = BuiltInModuleSlug | (string & {});

export interface ModulePricing {
  monthly: number;
  yearly: number;
  perpetual: number;
  currency: string;
  tiers: PricingTier[];
}

export interface PricingTier {
  name: string;
  price: number;
  features: string[];
  limits: Record<string, number>;
}

export interface ModuleSubscription {
  id: string;
  organization_id: string;
  module_id: string;
  plan: string;
  status: SubscriptionStatus;
  current_period_start: string;
  current_period_end: string;
  created_at: string;
}

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';

// ─── Subscription status guards (single source of truth) ─────────────────────
//
// `SubscriptionStatus` above is the canonical union, but the live database still
// holds rows written by schemas that predate it — most importantly the legacy
// Lexshift stack persisted trials as `'trial'` (not the canonical `'trialing'`),
// and some early modules used `'suspended'`/`'cancelled'`. Every runtime access
// guard (shell federation guard, module-subscription store, useModuleSubscription)
// must therefore accept BOTH spellings, or an organization on a `'trialing'`
// trial silently loses access to its module. These helpers are that shared
// contract so the acceptance rule lives in exactly one place instead of being
// re-implemented (and drifting) per call site.

/**
 * Runtime status strings that predate the canonical `SubscriptionStatus` union
 * but still occur in the database (e.g. the pre-migration Lexshift schema).
 */
export type LegacySubscriptionStatus = 'trial' | 'suspended' | 'cancelled';

/** Any status string a runtime guard may encounter (canonical + legacy). */
export type RuntimeSubscriptionStatus = SubscriptionStatus | LegacySubscriptionStatus;

/**
 * Status values that grant a module access when queried — canonical `'active'`
 * and `'trialing'`, plus the legacy `'trial'` spelling. Suitable for a
 * `.in('status', [...])` filter; trial expiry is then checked per-row.
 */
export const ACCESS_GRANTING_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'trial'] as const;

/** True when a status denotes a trial (canonical `'trialing'` or legacy `'trial'`). */
export function isTrialSubscriptionStatus(status: string | null | undefined): boolean {
  return status === 'trialing' || status === 'trial';
}

/** True when a status denotes an outright active (non-trial) subscription. */
export function isActiveSubscriptionStatus(status: string | null | undefined): boolean {
  return status === 'active';
}

/**
 * True when a status grants module access at all — active OR on a trial. Note
 * this does NOT check trial expiry; callers with a `trial_ends_at` must still
 * verify the trial window when {@link isTrialSubscriptionStatus} is true.
 */
export function grantsModuleAccess(status: string | null | undefined): boolean {
  return isActiveSubscriptionStatus(status) || isTrialSubscriptionStatus(status);
}

/**
 * Module registry entry — loaded from DB for dynamic (partner) modules.
 * Built-in modules are described by the shell's module registry
 * (apps/universe-shell/src/modules/registry.ts).
 */
export interface ModuleRegistryEntry {
  id: string;
  slug: string;
  name: string;
  deploy_url: string;
  icon?: string;
  description?: string;
  is_builtin: boolean;
  category?: string;
  version?: string;
}

// ─── Module Pricing System (super-admin managed) ─────────────────────────────

export type BillingType = 'monthly' | 'yearly' | 'perpetual' | 'on_premise';

export interface ModulePricingTier {
  id: string;
  name: string;
  name_pl?: string;
  slug: string;
  description?: string;
  description_pl?: string;
  sort_order: number;
  is_default: boolean;
  is_active: boolean;
  badge_text?: string;
  badge_text_pl?: string;
  badge_color?: string;
  created_at: string;
  updated_at: string;
}

export interface ModulePricingCurrency {
  code: string;
  name: string;
  symbol: string;
  is_active: boolean;
  sort_order: number;
}

export interface ModulePrice {
  id: string;
  module_id: string;
  tier_id: string;
  billing_type: BillingType;
  currency: string;
  amount: number;
  stripe_price_id?: string;
  stripe_price_active: boolean;
  features: string[];
  features_pl: string[];
  limits: Record<string, number>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined
  tier?: ModulePricingTier;
}

export interface ModulePriceInput {
  tier_id: string;
  billing_type: BillingType;
  currency: string;
  amount: number;
  features?: string[];
  features_pl?: string[];
  limits?: Record<string, number>;
  is_active?: boolean;
}

export interface ModuleWithPricing {
  id: string;
  name: string;
  description: string;
  long_description?: string;
  icon: string;
  version: string;
  category: string;
  tags: string[];
  status: string;
  is_public: boolean;
  author_type: string;
  deploy_url?: string;
  repository_url?: string;
  documentation_url?: string;
  screenshots: string[];
  featured: boolean;
  total_installs: number;
  avg_rating: number;
  review_count: number;
  support_email?: string;
  revenue_share_percent: number;
  stripe_product_id?: string;
  stripe_synced_at?: string;
  pricing_configured: boolean;
  created_at: string;
  updated_at: string;
  // Joined
  prices?: ModulePrice[];
}

// NOTE: Built-in module configuration (URLs, icons, routes) now lives in the
// shell's single module registry: apps/universe-shell/src/modules/registry.ts.
// The former `MODULE_CONFIGS` (a Module-Federation-era artifact with unused
// `remoteName`/`exposedModule` fields) was removed.

// =============================================================================
// Jedna lista organów platformy — repozytoryjny bliźniak katalogu z bazy.
//
// Powód, dla którego ta stała istnieje: na 04.09.2026 platforma miała CZTERY
// różne odpowiedzi na pytanie „ile jest organów" — 12 (`modules` z
// `module_kind='organ'`), 11 (rejestr powłoki, bez `print`), 11 z trzema
// organami zlepionymi w jeden rozdział (Akademia) i 17 (specyfikacje
// funkcjonalne, razem z wtyczkami). Bez jednej listy zdanie „kurs dla każdego
// organu" nie ma definicji, a bramka CI nie ma czego pilnować.
//
// ŹRÓDŁEM PRAWDY jest baza (`modules` gdzie `module_kind = 'organ'`). Ta stała
// jest jej odbiciem w repozytorium, bo test jednostkowy nie ma dostępu do
// bazy. Zgodność zmierzona 04.09.2026: dokładnie te dwanaście identyfikatorów,
// wszystkie ze `status = 'active'`. 02.10.2026 dochodzi `furnus` (migracja 740,
// `status = 'beta'`, decyzja właściciela Q45 w repo `insolitumai/smartbaker`):
// nazwa na bramce, rozdział kursu i temat egzaminu teorii są od razu, a zadanie
// praktyczne certyfikatu partnera — dopiero po becie (Q52, wpis w
// `docs/09-STRATEGIA-BIZNES/rejestr-dlugu-egzekwowania.json`). MANDATUM
// i PACKFLOW (też beta) zostają poza listą — nie mają ani panelu, ani kursu.
//
// Wtyczki integracyjne (`shopify`, `fakturownia`, `gopos`, `saldeo`) NIE są
// organami — nie mają własnego ekranu (`450:15-16`), więc nie mają też kursu;
// wchodzą jako rozdział kursu organu, który ich używa.
// =============================================================================

export const ORGANY_PLATFORMY = [
  'agenda',
  'artifex',
  'conspectus',
  'fabrica',
  'furnus',
  'gamification',
  'lexshift',
  'neurosense',
  'nexus',
  'print',
  'provisio',
  'ucs',
  'waterandice',
] as const;

export type OrganPlatformy = (typeof ORGANY_PLATFORMY)[number];

/** True dla identyfikatora, który jest organem platformy (a nie wtyczką). */
export function czyOrgan(id: string): id is OrganPlatformy {
  return (ORGANY_PLATFORMY as readonly string[]).includes(id);
}
